import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Alert,
  ScrollView,
  Modal,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Image,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";

const ManualVisitRedeem = ({ route, navigation }) => {
  const { CollabID } = route.params;

  const [collabDetails, setCollabDetails] = useState(null);
  const [venueCode, setVenueCode] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [dates, setDates] = useState([]);

  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [takePhotoModalVisible, setTakePhotoModalVisible] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [proofImageURL, setProofImageURL] = useState("");
  const [proofLocalPreview, setProofLocalPreview] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchCollabDetails = async () => {
      try {
        const response = await fetch(
          `https://collabbay.com/APIControllers/GetManualRedeemDetails.php?CollabID=${CollabID}`
        );
        const data = await response.json();

        if (data.success) {
          setCollabDetails(data.details);
        } else {
          Alert.alert("Error", data.message);
        }
      } catch (error) {
        console.error("Error fetching collab details:", error);
      }
    };

    fetchCollabDetails();
    generateDates();
  }, [CollabID]);

  const generateDates = () => {
    const today = new Date();
    const generatedDates = [];

    for (let i = 0; i < 30; i++) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      generatedDates.push(date.toDateString());
    }

    setDates(generatedDates);
  };

  const handleBackPress = () => {
    navigation.goBack();
  };

  const openFilePickerWeb = () => {
    try {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } catch (e) {
      Alert.alert("Error", "Could not open camera/file picker.");
    }
  };

  const onPickedWebFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Alert.alert("Invalid file", "Please choose an image.");
      return;
    }

    const localURL = URL.createObjectURL(file);
    setProofLocalPreview({ uri: localURL });

    await uploadProofToServer(file);
  };

  const openNativeCamera = async () => {
    try {
      const ImagePicker = require("expo-image-picker");
      const { status } = await ImagePicker.requestCameraPermissionsAsync();

      if (status !== "granted") {
        Alert.alert("Permission required", "Camera permission is needed.");
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        quality: 0.7,
        base64: false,
        exif: false,
      });

      if (result?.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        setProofLocalPreview({ uri: asset.uri });

        const file = await uriToFile(asset.uri, `visit_${Date.now()}.jpg`);
        await uploadProofToServer(file);
      }
    } catch (err) {
      Alert.alert("Unavailable", "Camera not available in this build.");
    }
  };

  const uriToFile = async (uri, fileName) => {
    const res = await fetch(uri);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type || "image/jpeg" });
  };

  const uploadProofToServer = async (file) => {
    try {
      setUploadingProof(true);

      const form = new FormData();
      form.append("CollabID", CollabID);
      form.append("image", file);

      const res = await fetch(
        "https://collabbay.com/APIControllers/UploadVisitProof.php",
        {
          method: "POST",
          body: form,
        }
      );

      const data = await res.json();

      if (data?.success && data?.image_url) {
        setProofImageURL(data.image_url);
        setTakePhotoModalVisible(false);
        setVenueCode("");
        Alert.alert(
          "Photo uploaded",
          "Your visit photo has been uploaded. Now select the date and time below."
        );
      } else {
        throw new Error(data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      Alert.alert("Upload failed", "Could not upload the photo. Please try again.");
    } finally {
      setUploadingProof(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const needsCode = !proofImageURL;

    if ((needsCode && !venueCode) || !selectedDate || !selectedTime) {
      let missing = [];
      if (needsCode && !venueCode) missing.push("Venue Code");
      if (!selectedDate) missing.push("Date");
      if (!selectedTime) missing.push("Time");

      setModalMessage(`Please fill in: ${missing.join(", ")}`);
      setIsSuccess(false);
      setModalVisible(true);
      setIsSubmitting(false);
      return;
    }

    const payloadDateTime = `${selectedDate} ${selectedTime}`;

    try {
      const endpoint = proofImageURL
        ? "https://collabbay.com/APIControllers/ManualInsertRedeemPhoto.php"
        : "https://collabbay.com/APIControllers/ManualInsertRedeem.php";

      const body = proofImageURL
        ? {
            CollabID: collabDetails.CollabID,
            DateTime: payloadDateTime,
            ProofImageURL: proofImageURL,
          }
        : {
            CollabID: collabDetails.CollabID,
            VenueCode: venueCode,
            DateTime: payloadDateTime,
          };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      const successMsg = proofImageURL
        ? "Visit logged with photo proof."
        : "Visit successfully logged.";

      const errorMsg = proofImageURL
        ? "Photo validation failed. Please try again."
        : "Invalid venue code. Ask venue staff for their 2-digit code.";

      setModalMessage(data.success ? successMsg : data.message || errorMsg);
      setIsSuccess(data.success);
      setModalVisible(true);

      if (data.success) {
        setVenueCode("");
        setSelectedDate("");
        setSelectedTime("");
        setProofImageURL("");
        setProofLocalPreview(null);
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalMessage("An error occurred. Please try again later.");
      setIsSuccess(false);
      setModalVisible(true);
      setIsSubmitting(false);
    }
  };

  const renderDatePicker = () => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.datePickerContainer}
      contentContainerStyle={styles.datePickerContent}
    >
      {dates.map((date, index) => (
        <TouchableOpacity
          key={index}
          style={[
            styles.dateOption,
            selectedDate === date && styles.dateOptionSelected,
          ]}
          onPress={() => setSelectedDate(date)}
        >
          <Text
            style={[
              styles.dateOptionText,
              selectedDate === date && styles.dateOptionTextSelected,
            ]}
          >
            {date}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderTimePicker = () => (
    <ScrollView style={styles.timeScrollView} showsVerticalScrollIndicator={false}>
      {Array.from({ length: 40 }, (_, i) => {
        const hour = Math.floor((i + 14) / 2) % 24;
        const minute = i % 2 === 0 ? "00" : "30";
        const ampm = hour >= 12 ? "PM" : "AM";
        const adjustedHour = hour % 12 === 0 ? 12 : hour % 12;
        const timeLabel = `${adjustedHour}:${minute} ${ampm}`;

        return (
          <TouchableOpacity
            key={i}
            style={[
              styles.timeSlot,
              selectedTime === timeLabel && styles.selectedTimeSlot,
            ]}
            onPress={() => setSelectedTime(timeLabel)}
          >
            <Text
              style={[
                styles.timeSlotText,
                selectedTime === timeLabel && styles.timeSlotTextSelected,
              ]}
            >
              {timeLabel}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  const showDateTimeSection = !!proofImageURL || venueCode.trim().length === 2;

  return (
    <View style={styles.page}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
            <Ionicons name="arrow-back" size={24} color="black" />
          </TouchableOpacity>

          <Text style={styles.pageTitle}>Log your visit</Text>

          <View style={styles.cameraCard}>
            <View style={styles.cardTopRow}>
              <View style={styles.cardTitleRow}>
                <View style={styles.primaryIconWrap}>
                  <Ionicons name="camera-outline" size={18} color="black" />
                </View>
                <Text style={styles.cardTitle}>Take a quick photo</Text>
              </View>

              <View style={styles.recommendedBadge}>
                <Text style={styles.recommendedBadgeText}>Recommended</Text>
              </View>
            </View>

            {!proofImageURL ? (
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={() => setTakePhotoModalVisible(true)}
              >
                <Ionicons name="camera" size={18} color="white" />
                <Text style={styles.primaryButtonText}>Open camera</Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.proofWrap}>
                <View style={styles.proofPill}>
                  <Ionicons name="checkmark-circle" size={18} color="green" />
                  <Text style={styles.proofPillText}>Photo uploaded</Text>
                </View>

                {proofLocalPreview?.uri ? (
                  <Image
                    source={{ uri: proofLocalPreview.uri }}
                    style={styles.previewImage}
                    resizeMode="cover"
                  />
                ) : null}

                <TouchableOpacity
                  onPress={() => {
                    setProofImageURL("");
                    setProofLocalPreview(null);
                  }}
                  style={styles.removePhotoButton}
                >
                  <Text style={styles.removePhotoButtonText}>Remove photo</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          <View style={styles.codeCard}>
            <View style={styles.cardTitleRow}>
              <View style={styles.secondaryIconWrap}>
                <Ionicons name="keypad-outline" size={18} color="black" />
              </View>
              <Text style={styles.codeCardTitle}>Can’t access your camera?</Text>
            </View>

            {!proofImageURL ? (
              <>
                <TextInput
                  style={styles.inputVenueCode}
                  value={venueCode}
                  onChangeText={setVenueCode}
                  keyboardType="numeric"
                  maxLength={2}
                  placeholder="_ _"
                  placeholderTextColor="#999"
                />
              </>
            ) : (
              <View style={styles.codeDisabledBox}>
                <Text style={styles.codeDisabledText}>
                  Photo added — no code needed.
                </Text>
              </View>
            )}
          </View>

          {showDateTimeSection && (
            <View style={styles.dateTimeCard}>
              <Text style={styles.sectionTitle}>Select date and time</Text>

              <Text style={styles.fieldLabel}>Date</Text>
              {renderDatePicker()}

              <Text style={styles.fieldLabel}>Time</Text>
              {renderTimePicker()}

              <TouchableOpacity
                style={[styles.confirmButton, isSubmitting && styles.confirmButtonDisabled]}
                onPress={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <View style={styles.buttonRow}>
                    <ActivityIndicator size="small" color="white" />
                    <Text style={styles.confirmButtonText}>Processing...</Text>
                  </View>
                ) : (
                  <View style={styles.buttonRow}>
                    <Ionicons name="checkmark-circle-outline" size={18} color="white" />
                    <Text style={styles.confirmButtonText}>Confirm</Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>
          )}

          {!collabDetails && (
            <Text style={styles.loadingText}>Loading...</Text>
          )}
        </View>
      </ScrollView>

      <Modal
        visible={takePhotoModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setTakePhotoModalVisible(false)}
      >
        <View style={styles.takePhotoModalContainer}>
          <View style={styles.takePhotoModalContent}>
            <Text style={styles.takePhotoTitle}>Take a quick photo</Text>

            {uploadingProof ? (
              <View style={styles.uploadingWrap}>
                <ActivityIndicator size="small" color="black" />
                <Text style={styles.uploadingText}>Uploading...</Text>
              </View>
            ) : (
              <>
                <TouchableOpacity
                  style={styles.cameraBtn}
                  onPress={() => {
                    if (Platform.OS === "web") {
                      openFilePickerWeb();
                    } else {
                      openNativeCamera();
                    }
                  }}
                >
                  <Ionicons name="camera" size={18} color="black" />
                  <Text style={styles.cameraBtnText}>Take photo</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.cancelBtn}
                  onPress={() => setTakePhotoModalVisible(false)}
                >
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>

        {Platform.OS === "web" && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={onPickedWebFile}
          />
        )}
      </Modal>

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.resultModalContainer}>
          <View style={styles.resultModalContent}>
            <Ionicons
              name={isSuccess ? "checkmark-circle" : "close-circle"}
              size={48}
              color={isSuccess ? "green" : "red"}
            />
            <Text style={styles.resultModalText}>{modalMessage}</Text>

            <TouchableOpacity
              style={styles.resultCloseButton}
              onPress={() => {
                setModalVisible(false);

                if (isSuccess && typeof window !== "undefined") {
                  try {
                    window.location.reload();
                  } catch (e) {}
                }
              }}
            >
              <Text style={styles.resultCloseButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  scrollContent: {
    paddingBottom: 40,
  },
  container: {
    width: "100%",
    maxWidth: 560,
    alignSelf: "center",
    paddingHorizontal: 18,
    paddingTop: 18,
  },
  backButton: {
    alignSelf: "flex-start",
    marginBottom: 10,
    marginTop: 6,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: "800",
    color: "black",
    marginBottom: 18,
  },
  loadingText: {
    textAlign: "center",
    color: "#666",
    marginTop: 14,
  },

  cameraCard: {
    backgroundColor: "#FDF05D",
    borderRadius: 22,
    padding: 18,
    marginBottom: 14,
  },
  codeCard: {
    backgroundColor: "#F3F3F3",
    borderRadius: 22,
    padding: 18,
    marginBottom: 14,
  },
  dateTimeCard: {
    backgroundColor: "#F3F3F3",
    borderRadius: 22,
    padding: 18,
    marginBottom: 14,
  },

  cardTopRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  cardTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
  primaryIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "rgba(255,255,255,0.6)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  secondaryIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "white",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: "800",
    color: "black",
  },
  codeCardTitle: {
    fontSize: 18,
    fontWeight: "800",
    color: "black",
  },
  recommendedBadge: {
    backgroundColor: "black",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
  },
  recommendedBadgeText: {
    color: "white",
    fontSize: 11,
    fontWeight: "700",
  },

  primaryButton: {
    backgroundColor: "black",
    borderRadius: 16,
    paddingVertical: 15,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  primaryButtonText: {
    color: "white",
    fontSize: 15,
    fontWeight: "800",
    marginLeft: 8,
  },

  proofWrap: {
    alignItems: "flex-start",
  },
  proofPill: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  proofPillText: {
    marginLeft: 8,
    fontWeight: "700",
    color: "black",
    fontSize: 13,
  },
  previewImage: {
    width: "100%",
    height: 190,
    borderRadius: 16,
    marginTop: 12,
  },
  removePhotoButton: {
    marginTop: 10,
  },
  removePhotoButtonText: {
    textDecorationLine: "underline",
    color: "black",
    fontWeight: "600",
  },

  inputVenueCode: {
    marginTop: 12,
    backgroundColor: "white",
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#E3E3E3",
    width: "100%",
    maxWidth: 260,
    alignSelf: "center",
    textAlign: "center",
    fontSize: 50,
    fontWeight: "700",
    color: "black",
    paddingVertical: 16,
    letterSpacing: 4,
  },
  codeDisabledBox: {
    marginTop: 12,
    backgroundColor: "white",
    borderRadius: 16,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  codeDisabledText: {
    color: "#333",
    fontWeight: "600",
    textAlign: "center",
  },

  sectionTitle: {
    fontSize: 20,
    fontWeight: "800",
    color: "black",
    marginBottom: 14,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "700",
    color: "black",
    marginBottom: 8,
    marginTop: 2,
  },

  datePickerContainer: {
    marginBottom: 16,
  },
  datePickerContent: {
    paddingRight: 8,
  },
  dateOption: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginRight: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#D8D8D8",
    backgroundColor: "white",
  },
  dateOptionSelected: {
    backgroundColor: "#FDF05D",
    borderColor: "#FDF05D",
  },
  dateOptionText: {
    fontSize: 14,
    color: "black",
    fontWeight: "600",
  },
  dateOptionTextSelected: {
    color: "black",
  },

  timeScrollView: {
    height: 150,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E6E6E6",
    backgroundColor: "white",
    marginBottom: 18,
  },
  timeSlot: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderColor: "#EEEEEE",
  },
  selectedTimeSlot: {
    backgroundColor: "#FDF05D",
  },
  timeSlotText: {
    fontSize: 15,
    color: "black",
    fontWeight: "500",
  },
  timeSlotTextSelected: {
    fontWeight: "700",
  },

  confirmButton: {
    backgroundColor: "black",
    borderRadius: 18,
    paddingVertical: 15,
    alignItems: "center",
    justifyContent: "center",
  },
  confirmButtonDisabled: {
    opacity: 0.6,
  },
  buttonRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  confirmButtonText: {
    color: "white",
    fontSize: 17,
    fontWeight: "800",
    marginLeft: 8,
  },

  takePhotoModalContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
    backgroundColor: "rgba(0,0,0,0.35)",
  },
  takePhotoModalContent: {
    backgroundColor: "white",
    borderRadius: 18,
    padding: 22,
    width: "100%",
    maxWidth: 420,
  },
  takePhotoTitle: {
    fontSize: 20,
    fontWeight: "800",
    textAlign: "center",
    color: "black",
    marginBottom: 16,
  },
  cameraBtn: {
    backgroundColor: "#FDF05D",
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  cameraBtnText: {
    marginLeft: 8,
    fontWeight: "700",
    color: "black",
    fontSize: 15,
  },
  cancelBtn: {
    marginTop: 10,
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F3F3F3",
  },
  cancelBtnText: {
    fontWeight: "700",
    color: "black",
    fontSize: 15,
  },
  uploadingWrap: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
  },
  uploadingText: {
    marginTop: 10,
    color: "#333",
    fontSize: 14,
  },

  resultModalContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.35)",
    paddingHorizontal: 20,
  },
  resultModalContent: {
    backgroundColor: "white",
    padding: 22,
    borderRadius: 18,
    alignItems: "center",
    width: "100%",
    maxWidth: 380,
  },
  resultModalText: {
    fontSize: 16,
    marginVertical: 15,
    textAlign: "center",
    color: "black",
    lineHeight: 22,
  },
  resultCloseButton: {
    backgroundColor: "black",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 14,
    marginTop: 6,
    width: "100%",
  },
  resultCloseButtonText: {
    color: "white",
    fontSize: 16,
    textAlign: "center",
    fontWeight: "800",
  },
});

export default ManualVisitRedeem;